<script setup lang="ts" generic="T, S extends ReadonlyArray<string>">
import { computed, readonly, ref, shallowRef, toRef, type VNode, watch } from 'vue';

import SkeletonRowContextProvider from '@/kasten-components/components/KaTable/SkeletonRowContextProvider.vue';
import type { WithPartialKeys } from '@/kasten-components/types/tsUtils';

import KaTableCell from './KaTableCell.vue';
import { provideTableContext, type SelectAllState, type SortState } from './model';
import RowContextProvider from './RowContextProvider.vue';
import { useMaybeVirtualizedRows, type VirtualizationSettings } from './useMaybeVirtualizedRows';

type Props = {
  data: T[];
  // Uniq key getter for each row
  keyGetter: (item: T, index: number) => string;
  zebra?: boolean;
  small?: boolean;
  loading?: {
    state: boolean;
    rowLength?: number;
  };
  // Specify this to enable sort
  sort?: {
    // Tuple of sortable column names
    columns: S;
    // Default sort column
    defaultColumn: S[number];
    // Default sort direction
    defaultDesc?: boolean;
    // Sort function. Keep in mind that Array.prototype.sort is in-place sort.
    onSort?: (data: T[], isDesc: boolean, columnName: S[number]) => T[];
  };
  selectedRowKeys?: Iterable<string>;
  disabledRowKeys?: Iterable<string>;
  // Options for virtualization.
  virtualized?: WithPartialKeys<VirtualizationSettings<T>, 'rowHeightGetter'>;
  ariaLabelledBy?: string;
  readOnly?: boolean;
  caption?: string;
};

const props = withDefaults(defineProps<Props>(), {
  readOnly: false,
  ariaLabelledBy: undefined,
  zebra: true,
  small: false,
  caption: '',
});

const sortDataFunc = computed(() => props.sort?.onSort ?? ((data: T[]) => data));

const slots = defineSlots<{
  caption(): VNode;
  head(): VNode;
  skeletonRow(props: { index: number }): VNode;
  bodyRow(props: { item: T; index: number }): VNode;
  footer(): VNode;
  empty(): VNode;
}>();

const emit = defineEmits<{
  // Is called to notify whenever sort is triggered by user
  (e: 'sort', columnName: string, isDesc: boolean): void;
  // Is called when the user scrolls to the end of the scroll container
  (e: 'loadMore'): void;
  // Is called whenever a user changes the selection
  (e: 'toggleRow', key: string, wasSelected: boolean, toggledItem: T): void;
  (e: 'toggleAllRows', wasSelected: boolean, toggledItemsMap: Map<string, T>): void;
  // Is called when selection is changed either by user or when dataset is updated by the parent component though props
  (e: 'update:selectedRowKeys', items: Set<string>): void;
}>();

const tableRef = ref<HTMLTableElement>();
const bodyRef = ref<HTMLTableSectionElement | null>();

const sortState = ref<SortState>({
  isDesc: props.sort?.defaultDesc ?? false,
  currentColumn: props.sort?.defaultColumn ?? '',
  sortableColumns: new Set(props.sort?.columns ?? []),
});

const isSmall = readonly(toRef(props, 'small'));

const internalData = shallowRef(
  sortDataFunc.value(props.data, sortState.value.isDesc, sortState.value.currentColumn),
);

const isMultiSelectable = ref(false);

const columns = ref<Set<string>>(new Set());

const selectableRows = ref<Set<string>>(new Set());
const selectedKeys = ref<Set<string>>(new Set());

const isLoading = computed(() => props.loading?.state === true);

const keyToItem = computed(
  () =>
    new Map<string, T>(
      internalData.value.map((item, index) => [props.keyGetter(item, index), item]),
    ),
);
const keys = computed(() => [...keyToItem.value.keys()]);

const { measureElement, virtualOffsetTop, virtualOffsetBottom, virtualizedOrNotRows } =
  useMaybeVirtualizedRows(
    internalData,
    keys,
    props.virtualized !== undefined
      ? { rowHeightGetter: isSmall.value ? () => 40 : () => 45, ...props.virtualized }
      : undefined,
    bodyRef,
    tableRef,
  );

const isSelectable = computed(() => selectableRows.value.size > 0);

const disabledKeys = computed(() => new Set(props.disabledRowKeys));
const dataCount = computed(() => internalData.value.length);
const tableIsEmpty = computed(() => dataCount.value === 0);

// If all non disabled rows are selected
const selectedAll = computed<SelectAllState>(() => {
  const selectableKeys = keys.value.filter(
    (key) => selectableRows.value.has(key) && !disabledKeys.value.has(key),
  );
  return selectableKeys.length > 0 && selectableKeys.every((key) => selectedKeys.value.has(key));
});
const selectedSome = computed(() => !selectedAll.value && selectedKeys.value.size > 0);

const handleToggleAll = (newValue?: boolean) => {
  const wasSelected = newValue ?? !selectedAll.value;

  // Filter out disabled keys when toggling
  const toggleableRows = new Map(
    Array.from(keyToItem.value.entries()).filter(([key]) => !disabledKeys.value.has(key)),
  );

  emit('toggleAllRows', wasSelected, toggleableRows);

  // Update separate toggle states. As `selectedKeys` can be a bigger partially overlapping list perform atomic update
  keys.value.forEach((key) => {
    if (!disabledKeys.value.has(key)) {
      wasSelected ? selectedKeys.value.add(key) : selectedKeys.value.delete(key);
    }
  });

  reconsiderSelectedRows();
};
const handleToggleRow = (key: string, newValue?: boolean) => {
  const toggledItem = keyToItem.value.get(key)!;
  const wasSelected = newValue ?? selectedKeys.value.has(key);

  emit('toggleRow', key, wasSelected, toggledItem);

  wasSelected ? selectedKeys.value.add(key) : selectedKeys.value.delete(key);
  reconsiderSelectedRows();
};
const handleIncludeColumn = (name: string, toInclude: boolean) =>
  toInclude ? columns.value.add(name) : columns.value.delete(name);
const handleIncludeSelectableRow = (key: string, isSelectable: boolean) => {
  isSelectable ? selectableRows.value.add(key) : selectableRows.value.delete(key);
};
const handleSort = (columnName: string, isDesc?: boolean) => {
  const sortDesc =
    isDesc ?? (sortState.value.currentColumn === columnName && !sortState.value.isDesc);

  internalData.value = sortDataFunc.value(internalData.value, sortDesc, columnName);
  sortState.value = {
    ...sortState.value,
    isDesc: sortDesc,
    currentColumn: columnName,
  };

  emit('sort', columnName, sortDesc);
};

const isRowSelected = (key: string) => selectedKeys.value.has(key);
const isRowSelectable = (key: string) =>
  selectableRows.value.has(key) && !disabledKeys.value.has(key);

const reconsiderSelectedRows = () => {
  const overlappingSelectedKeys = [...selectedKeys.value.values()].filter((key) =>
    keyToItem.value.has(key),
  );
  emit('update:selectedRowKeys', new Set(overlappingSelectedKeys));
};

provideTableContext({
  columns: readonly(columns),
  isLoading: readonly(isLoading),
  isSmall,
  isZebra: readonly(toRef(props, 'zebra')),
  isMultiSelectable: readonly(isMultiSelectable),
  selectedAllState: readonly(selectedAll),
  selectedSomeState: readonly(selectedSome),
  sortState: readonly(sortState),
  isTableEmpty: readonly(tableIsEmpty),
  isSelectable: readonly(isSelectable),
  onToggleAll: handleToggleAll,
  onIncludeColumn: handleIncludeColumn,
  onToggleRow: handleToggleRow,
  onIncludeSelectableRow: handleIncludeSelectableRow,
  onSort: handleSort,
  setIsMultiSelectable: (value: boolean) => (isMultiSelectable.value = value),
  isRowSelected,
  isRowSelectable,
});

// Watch after sortable columns update from parent component
watch(
  () => props.sort?.columns,
  (columns) => {
    sortState.value.sortableColumns = new Set(columns ?? []);
  },
  { deep: true, immediate: true },
);
// Update and sort internal data when data from props is updated
watch(
  () => props.data,
  (newData) => {
    internalData.value = sortDataFunc.value(
      newData,
      sortState.value.isDesc,
      sortState.value.currentColumn,
    );
  },
  { deep: true },
);
// Update selected keys when selectedItems is updated from parent
watch(
  () => props.selectedRowKeys,
  (keys = []) => (selectedKeys.value = new Set(keys)),
  { immediate: true, deep: true },
);
</script>
<template>
  <table
    ref="tableRef"
    role="grid"
    v-bind="$attrs"
    :aria-multiselectable="isMultiSelectable"
    :aria-labelledby="props.ariaLabelledBy"
    :aria-readonly="props.readOnly"
    :aria-rowcount="internalData.length"
    class="relative w-full border-separate border-spacing-0 overflow-visible rounded-lg bg-white text-gray-900 dark:bg-navy-950 dark:text-gray-300"
  >
    <slot name="caption">
      <caption v-if="props.caption" class="sr-only">
        {{
          props.caption
        }}
      </caption>
    </slot>
    <thead v-if="'head' in slots">
      <slot name="head" />
    </thead>
    <tbody v-if="virtualizedOrNotRows.length > 0 || isLoading" ref="bodyRef">
      <template v-if="isLoading">
        <SkeletonRowContextProvider
          v-for="(_, index) in new Array(props.loading?.rowLength ?? 10).fill(undefined)"
          :key="index"
          :index="index"
        >
          <slot name="skeletonRow" :index="index" />
        </SkeletonRowContextProvider>
      </template>
      <template v-else>
        <tr v-if="virtualOffsetTop > 0" key="paddingTop" role="presentation">
          <td role="presentation" colspan="100%" :style="{ height: virtualOffsetTop + 'px' }" />
        </tr>
        <RowContextProvider
          v-for="row in virtualizedOrNotRows"
          :key="String(row.key)"
          :index="row.index"
          :row-key="row.key as string"
          :measure-element="measureElement"
        >
          <slot name="bodyRow" :item="internalData[row.index]" :index="row.index" />
        </RowContextProvider>
        <tr v-if="virtualOffsetBottom > 0" key="paddingBottom" role="presentation">
          <td role="presentation" colspan="100%" :style="{ height: virtualOffsetBottom + 'px' }" />
        </tr>
      </template>
    </tbody>
    <tr v-if="!isLoading && tableIsEmpty" class="group/row">
      <KaTableCell colspan="100%">
        <slot name="empty" />
      </KaTableCell>
    </tr>
  </table>
</template>
